import './rc-onboarding-step-document';
import './rc-onboarding-step-profile';
import './rc-onboarding-step-property';
import './rc-onboarding-step-reminder';
import './rc-onboarding-step-tenant';
import { type PropertyDto, type ProfileDto, type ReminderDto, type TenantDto } from '../../services/api';
import { t } from '../../services/i18n';
import { STEPS, getOnboardingSteps } from './onboarding-shared';
import { RcOnboardingStepDocument } from './rc-onboarding-step-document';
import { RcOnboardingStepProfile } from './rc-onboarding-step-profile';
import { RcOnboardingStepProperty } from './rc-onboarding-step-property';
import { RcOnboardingStepReminder } from './rc-onboarding-step-reminder';
import { RcOnboardingStepTenant } from './rc-onboarding-step-tenant';

export class RcOnboardingShell extends HTMLElement {
  completionDestination: 'dashboard' | 'checkout' = 'dashboard';
  currentStep = 0;
  totalSteps: number = STEPS.length;
  progressPct = 0;
  profile: ProfileDto | null = null;
  createdProperty: PropertyDto | null = null;
  createdTenant: TenantDto | null = null;
  createdTenantRentAmount: number | null = null;
  createdTenantStartDate: string | null = null;
  createdDocumentId: string | null = null;
  createdDocumentName: string | null = null;
  createdReminder: ReminderDto | null = null;
  csvImportNotice: string | null = null;
  csvImportErrors: string[] = [];
  isImportingCsv = false;
  currentUserName = '';

  render(): void {
    const steps = getOnboardingSteps();
    const step = steps[this.currentStep];
    this.innerHTML = `
      <div class="wizard-shell">
        <div class="wizard-sidebar">
          <div class="wizard-brand">Rentomax</div>
          <div class="wizard-sidebar-title">${t('onboarding.sidebarTitle')}</div>
          <p class="wizard-sidebar-sub">${t('onboarding.sidebarSubtitle', { count: this.totalSteps })}</p>
          <nav class="wizard-steps-nav">
            ${steps.map((item, index) => `
              <div class="wizard-step-nav-item ${index === this.currentStep ? 'active' : ''} ${index < this.currentStep ? 'done' : ''}">
                <div class="wizard-step-nav-dot">
                  ${index < this.currentStep ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6 9 17l-5-5"/></svg>` : `<span>${index + 1}</span>`}
                </div>
                <div class="wizard-step-nav-label">
                  <span class="wizard-step-nav-icon">${item.icon}</span>
                  <span class="wizard-step-nav-name">${item.label}</span>
                </div>
              </div>
            `).join('')}
          </nav>
          <div class="wizard-sidebar-footer">
            <div class="wizard-progress-bar-wrap">
              <div class="wizard-progress-bar" style="width: ${this.progressPct}%"></div>
            </div>
            <span class="wizard-progress-label">${t('onboarding.progressLabel', { completed: this.currentStep, total: this.totalSteps })}</span>
          </div>
        </div>

        <div class="wizard-main">
          <div class="wizard-header">
            <div class="wizard-step-badge">${step.icon} ${t('onboarding.stepBadge', { current: this.currentStep + 1, total: this.totalSteps })}</div>
            <button type="button" class="btn-logout wizard-logout-btn" data-action="logout">${t('auth.logout')}</button>
          </div>
          <div class="wizard-content" id="wizard-content">
            ${this.renderStepHost()}
          </div>
        </div>
      </div>
    `;

    this.renderStep();
  }

  private renderStepHost(): string {
    switch (STEPS[this.currentStep].id) {
      case 'profile': return '<rc-onboarding-step-profile></rc-onboarding-step-profile>';
      case 'property': return '<rc-onboarding-step-property></rc-onboarding-step-property>';
      case 'tenant': return '<rc-onboarding-step-tenant></rc-onboarding-step-tenant>';
      case 'document': return '<rc-onboarding-step-document></rc-onboarding-step-document>';
      case 'reminder': return '<rc-onboarding-step-reminder></rc-onboarding-step-reminder>';
      default: return '';
    }
  }

  private renderStep(): void {
    switch (STEPS[this.currentStep].id) {
      case 'profile': {
        const step = this.querySelector('rc-onboarding-step-profile') as RcOnboardingStepProfile | null;
        if (!step) return;
        step.profile = this.profile;
        step.currentUserName = this.currentUserName;
        step.render();
        return;
      }
      case 'property': {
        const step = this.querySelector('rc-onboarding-step-property') as RcOnboardingStepProperty | null;
        if (!step) return;
        step.createdProperty = this.createdProperty;
        step.csvImportNotice = this.csvImportNotice;
        step.csvImportErrors = this.csvImportErrors;
        step.isImportingCsv = this.isImportingCsv;
        step.render();
        return;
      }
      case 'tenant': {
        const step = this.querySelector('rc-onboarding-step-tenant') as RcOnboardingStepTenant | null;
        if (!step) return;
        step.createdProperty = this.createdProperty;
        step.createdTenant = this.createdTenant;
        step.createdTenantRentAmount = this.createdTenantRentAmount;
        step.createdTenantStartDate = this.createdTenantStartDate;
        step.render();
        return;
      }
      case 'document': {
        const step = this.querySelector('rc-onboarding-step-document') as RcOnboardingStepDocument | null;
        if (!step) return;
        step.createdDocumentId = this.createdDocumentId;
        step.createdDocumentName = this.createdDocumentName;
        step.render();
        return;
      }
      case 'reminder': {
        const step = this.querySelector('rc-onboarding-step-reminder') as RcOnboardingStepReminder | null;
        if (!step) return;
        step.createdReminder = this.createdReminder;
        step.completionDestination = this.completionDestination;
        step.render();
        return;
      }
      default:
        return;
    }
  }
}

if (!customElements.get('rc-onboarding-shell')) {
  customElements.define('rc-onboarding-shell', RcOnboardingShell);
}
